// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"
import { autoLayout } from "../../../lib/flow/auto_layout.js"
import { resolveFlowEngine } from "../../../lib/flow/utils.js"

// Layout controller — auto-layout buttons for vertical/horizontal arrangement.
export default class extends Controller {
  static outlets = ["ui--flow"]

  static values = {
    direction: { type: String, default: "TB" },
    nodeSpacing: { type: Number, default: 80 },
    rankSpacing: { type: Number, default: 150 }
  }

  connect() {
    this.element.classList.add("ui-flow-layout-controls")
    // #15: every timer this controller schedules is tracked here — the
    // transition-cleanups and the delayed fitView previously fired into a
    // potentially-disconnected controller (or onto a torn-down canvas).
    this._timers = []
    this._disconnected = false
  }

  disconnect() {
    this._disconnected = true
    this._timers?.forEach((t) => clearTimeout(t))
    this._timers = []
  }

  // Tracked timeout: self-cancels if disconnect() runs first, no-ops on a
  // disconnected controller even if a race lets it fire.
  _later(fn, delay) {
    const timers = this._timers || []
    const t = setTimeout(() => {
      this._timers = (this._timers || []).filter((x) => x !== t)
      if (this._disconnected) return
      fn()
    }, delay)
    timers.push(t)
    this._timers = timers
  }

  layoutVertical()   { this._applyLayout("TB") }
  layoutHorizontal() { this._applyLayout("LR") }

  _applyLayout(direction) {
    const flow = this._getFlow()
    if (!flow) {
      // F10: no resolvable engine must be a no-op with a warning, never a
      // crash (the old bare-return died silently; the old legacy-id fallback
      // could also resolve the WRONG canvas on multi-canvas pages).
      console.warn("ui--flow-layout: no ui--flow engine resolvable (outlet attribute missing?) — layout skipped")
      return
    }

    const nodes = flow.getNodes()
    const edges = flow.getEdges()

    if (nodes.length === 0) return

    // Get current node dimensions from DOM
    const nodeDims = new Map()
    if (flow.hasNodesTarget) {
      flow.nodesTarget.querySelectorAll("[data-ui--flow-node]").forEach(el => {
        nodeDims.set(el.dataset["ui-FlowNode"], {
          width: el.offsetWidth || 150,
          height: el.offsetHeight || 60
        })
      })
    }

    const avgWidth = nodeDims.size > 0
      ? Array.from(nodeDims.values()).reduce((s, d) => s + d.width, 0) / nodeDims.size
      : 150
    const avgHeight = nodeDims.size > 0
      ? Array.from(nodeDims.values()).reduce((s, d) => s + d.height, 0) / nodeDims.size
      : 60

    const positions = autoLayout(nodes, edges, {
      direction,
      nodeSpacing: this.nodeSpacingValue,
      rankSpacing: this.rankSpacingValue,
      nodeWidth: avgWidth,
      nodeHeight: avgHeight
    })

    // F10: route every move through the engine so the node MAP carries the new
    // positions (toObject/save/undo see them) — the old direct style writes
    // were invisible to the engine. One undo entry covers the whole layout;
    // _pushUndo is the engine's only history API today (see report).
    flow._pushUndo?.()

    // Animate nodes to new positions
    if (flow.hasNodesTarget) {
      positions.forEach((pos, id) => {
        const el = flow.nodesTarget.querySelector(`[data-ui--flow-node="${CSS.escape(id)}"]`)
        if (el) {
          el.style.transition = "left 0.4s ease, top 0.4s ease"

          // Remove transition after animation (tracked — a disconnect before
          // the 450ms mark must not resurrect a dead callback).
          this._later(() => {
            el.style.transition = ""
          }, 450)
        }
        // moveNode writes style.left/top AND the map entry, and emits
        // nodemove so edges/minimap follow (the old code emitted nodemove
        // without ever updating the map).
        flow.moveNode(id, pos.x, pos.y)
      })
    }

    // Fit view after layout: re-resolve the engine at fire time — a canvas
    // torn down during the 500ms window must not be driven by a stale ref.
    // No-arg fitView (UX audit): the chrome-aware path — padding becomes the
    // engine's fitViewPadding (50) + chrome insets instead of the legacy
    // explicit 60, and the zoom caps at fitViewMaxZoom.
    this._later(() => { const f = this._getFlow(); if (f) f.fitView() }, 500)

    this._emit("layout", { direction, positions: Object.fromEntries(positions) })
  }

  _getFlow() { return resolveFlowEngine(this) }

  _emit(name, detail) {
    document.dispatchEvent(new CustomEvent(`ui--flow:${name}`, { detail, bubbles: true }))
  }
}
